import React, { useMemo, useState } from "react"
import { Formik, useFormikContext } from "formik"
import { Box, InputBase, Popover, TextField, Typography } from "@mui/material"
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown"
import BusinessIcon from "@mui/icons-material/Business"
import CalendarTodayIcon from "@mui/icons-material/CalendarToday"
import SearchIcon from "@mui/icons-material/Search"
import { DatePicker, PickersDay } from "@mui/x-date-pickers"
import { styled } from "@mui/material/styles"
import moment from "moment"
import { COLORS } from "../../../../utils"
import EnterpriseBadge from "../../../../components/Organizations/EnterpriseBadge"
import DeliveryModal from "./DeliveryModal"

const toWeekLabel = (date) => {
  const m = moment(date)
  const start = m.clone().startOf("isoWeek")
  const end = m.clone().endOf("isoWeek")
  return `S${m.isoWeek()} (${start.format("D")} — ${end.format("D MMM YYYY")})`
}

const toISOWeek = (date) => {
  const m = moment(date)
  return `${m.isoWeekYear()}-W${String(m.isoWeek()).padStart(2, "0")}`
}

const CustomPickersDay = styled(PickersDay, {
  shouldForwardProp: (p) => p !== "dayIsBetween" && p !== "isFirstDay" && p !== "isLastDay",
})(({ theme, dayIsBetween, isFirstDay, isLastDay }) => ({
  ...(dayIsBetween && {
    borderRadius: 0,
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    "&:hover, &:focus": { backgroundColor: theme.palette.primary.dark },
  }),
  ...(isFirstDay && { borderTopLeftRadius: "50%", borderBottomLeftRadius: "50%" }),
  ...(isLastDay && { borderTopRightRadius: "50%", borderBottomRightRadius: "50%" }),
}))

const EnterpriseField = ({ enterprises }) => {
  const { values, setFieldValue } = useFormikContext()
  const [anchorEl, setAnchorEl] = useState(null)
  const [search, setSearch] = useState("")

  const selected = enterprises.find((e) => e.objectId === values.enterpriseId) || null
  const open = Boolean(anchorEl)

  const filtered = useMemo(() => {
    const q = search.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "")
    return enterprises.filter((e) =>
      (e.name || "").toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "").includes(q)
    )
  }, [enterprises, search])

  const handleOpen = (e) => { setAnchorEl(e.currentTarget); setSearch("") }
  const handleClose = () => setAnchorEl(null)
  const handleSelect = (enterprise) => { setFieldValue("enterpriseId", enterprise.objectId); handleClose() }

  return (
    <>
      <TextField
        label="Entreprise"
        variant="standard"
        value={selected ? selected.name : ""}
        placeholder="Sélectionner une entreprise..."
        onClick={handleOpen}
        inputProps={{ readOnly: true, style: { cursor: "pointer" } }}
        InputProps={{
          startAdornment: selected ? <EnterpriseBadge enterprise={selected} size={20} /> : null,
          endAdornment: <ArrowDropDownIcon sx={{ color: "text.secondary", transform: open ? "rotate(180deg)" : "none", transition: "transform 200ms", fontSize: 20 }} />,
        }}
        fullWidth
      />
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{ paper: { sx: { width: anchorEl ? anchorEl.offsetWidth : undefined, minWidth: 240, maxHeight: 340, display: "flex", flexDirection: "column" } } }}
      >
        <Box sx={sx.searchRow}>
          <SearchIcon sx={sx.searchIcon} />
          <InputBase value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher…" autoFocus fullWidth inputProps={{ autoComplete: "off" }} sx={sx.searchInput} />
        </Box>
        <Box sx={sx.optionList}>
          {filtered.map((e) => (
            <Box key={e.objectId} role="option" aria-selected={values.enterpriseId === e.objectId} onClick={() => handleSelect(e)} className="flexRow center" sx={{ ...sx.option, ...(values.enterpriseId === e.objectId && sx.optionSelected) }}>
              <EnterpriseBadge enterprise={e} size={20} />
              <Box component="span" sx={{ ml: 1 }}>{e.name}</Box>
            </Box>
          ))}
          {filtered.length === 0 && <Box sx={sx.noOption}>Aucune entreprise trouvée</Box>}
        </Box>
      </Popover>
    </>
  )
}

const WeekPickerField = () => {
  const { setFieldValue } = useFormikContext()
  const [weekDate, setWeekDate] = useState(null)
  const minDate = moment().startOf("isoWeek")

  const renderWeekDay = (day, _selected, pickersDayProps) => {
    if (!weekDate) return <PickersDay {...pickersDayProps} />
    const start = moment(weekDate).startOf("isoWeek")
    const end = moment(weekDate).endOf("isoWeek")
    const dayIsBetween = moment(day).isBetween(start, end, null, "[]")
    const isFirstDay = moment(day).isSame(start, "day")
    const isLastDay = moment(day).isSame(end, "day")
    return (
      <CustomPickersDay {...pickersDayProps} disableMargin dayIsBetween={dayIsBetween} isFirstDay={isFirstDay} isLastDay={isLastDay} showDaysOutsideCurrentMonth />
    )
  }

  const handleChange = (date) => {
    setWeekDate(date)
    setFieldValue("week", date && moment(date).isValid() ? toISOWeek(date) : "")
  }

  return (
    <DatePicker
      value={weekDate}
      onChange={handleChange}
      minDate={minDate}
      renderDay={renderWeekDay}
      calendarStartDay={1}
      components={{ OpenPickerIcon: () => <CalendarTodayIcon sx={{ fontSize: 20 }} /> }}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Semaine"
          variant="standard"
          inputProps={{
            ...params.inputProps,
            value: weekDate ? toWeekLabel(weekDate) : "",
            placeholder: "Sélectionner une semaine",
          }}
        />
      )}
    />
  )
}

const DeliveryEnterpriseModal = ({ open, onClose, onCancel, enterprises = [], onGoToSettings, initialValues, onSubmit }) => {
  const [error, setError] = useState(null)
  const activeEnterprises = enterprises.filter((e) => e.status !== "ARCHIVED")

  if (activeEnterprises.length === 0) {
    return (
      <DeliveryModal open={open} onClose={onClose} title="Aucune entreprise" leftLabel="Annuler" onLeft={onCancel} submitLabel="Aller dans les paramètres" onSubmit={onGoToSettings}>
        <Box sx={sx.emptyIcon}><BusinessIcon sx={{ fontSize: 40, color: "text.disabled" }} /></Box>
        <Typography variant="subtitle1" fontWeight={700} gutterBottom textAlign="center">
          Configurez d'abord une entreprise
        </Typography>
        <Typography variant="body2" color="text.secondary" textAlign="center">
          Aucune entreprise active. Créez d'abord une entreprise dans les paramètres.
        </Typography>
      </DeliveryModal>
    )
  }

  return (
    <Formik initialValues={{ enterpriseId: initialValues.enterpriseId, week: initialValues.week }} enableReinitialize>
      {({ values }) => {
        const handleNext = () => {
          setError(null)
          if (!values.enterpriseId) { setError("Veuillez sélectionner une entreprise"); return }
          if (!values.week) { setError("Veuillez sélectionner une semaine"); return }
          const ent = activeEnterprises.find((e) => e.objectId === values.enterpriseId)
          if (!ent || !(ent.distributionCenters || []).length) { setError("Impossible de créer une livraison pour une entreprise n'ayant aucun site de livraison rattaché."); return }
          onSubmit({ enterpriseId: values.enterpriseId, week: values.week })
        }
        return (
          <DeliveryModal
            open={open}
            onClose={onClose}
            title="Créer une carte"
            subtitle="Sélectionnez une entreprise et la semaine de la carte."
            leftLabel="Annuler"
            onLeft={onCancel}
            submitLabel="Suivant"
            onSubmit={handleNext}
          >
            <EnterpriseField enterprises={activeEnterprises} />
            <WeekPickerField />
            {error && <Typography variant="caption" color="error">{error}</Typography>}
          </DeliveryModal>
        )
      }}
    </Formik>
  )
}

const sx = {
  emptyIcon: {
    width: 80,
    height: 80,
    borderRadius: "50%",
    backgroundColor: COLORS.LIGHT_GREY_3,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    mx: "auto",
    mb: 2,
  },
  searchRow: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    px: "12px",
    py: "8px",
    borderBottom: "1px solid",
    borderColor: "divider",
    flexShrink: 0,
  },
  searchIcon: { fontSize: 18, color: "text.disabled" },
  searchInput: { fontSize: 14 },
  optionList: { overflowY: "auto" },
  option: {
    px: "16px",
    py: "10px",
    fontSize: 14,
    cursor: "pointer",
    "&:hover": { bgcolor: "action.hover" },
  },
  optionSelected: { color: "primary.main", fontWeight: 600, bgcolor: "primary.50" },
  noOption: { px: 2, py: 1.5, fontSize: 14, color: "text.disabled", textAlign: "center" },
}

export default DeliveryEnterpriseModal
